import React, { useState, useCallback, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import axios from 'axios'
import TopRail from '../DashboardV2/TopRail'
import UnclaimedWorkList from './UnclaimedWorkList'
import UnclaimedWorkDetail from './UnclaimedWorkDetail'
import { socket } from '../../SocketIo/Socket'
import '../../../styles/hmiReskin.css'
import '../DashboardV2/dashboardV2.css'
import './unclaimedWork.css'

/* Tab key -> API state param mapping */
const TAB_STATE_MAP = {
  submitted: 'submitted',
  assigned: 'assigned',
  completed: 'completed',
}

function UnclaimedWorkPage(props) {
  const { url, user } = props
  const currentUserId = user?._id

  const [items, setItems] = useState([])
  const [stats, setStats] = useState({ submitted: 0, assigned: 0, urgent: 0, completedToday: 0 })
  const [activeTab, setActiveTab] = useState('submitted')
  const [filters, setFilters] = useState({ priority: '', source: '', service_type: '', sort: 'oldest' })
  const [selectedId, setSelectedId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  const isMounted = useRef(true)
  useEffect(() => () => { isMounted.current = false }, [])

  const showToast = useCallback((msg) => {
    setToastMessage(msg)
    setToastVisible(true)
    setTimeout(() => { if (isMounted.current) setToastVisible(false) }, 3000)
  }, [])

  /* ---- Fetch items ---- */
  const fetchItems = useCallback(async () => {
    if (!url) return
    setLoading(true)
    try {
      const params = { state: TAB_STATE_MAP[activeTab] }
      if (filters.priority) params.priority = filters.priority
      if (filters.source) params.source = filters.source
      if (filters.service_type) params.service_type = filters.service_type
      if (filters.sort === 'newest') params.sort = 'newest'
      else params.sort = 'oldest'

      const res = await axios.get(`${url}/api/hmi/unclaimed-work`, { params })
      if (res.data?.success && isMounted.current) {
        setItems(res.data.data || [])
      }
    } catch (err) {
      console.error('[UW_PAGE] Failed to load items:', err)
    }
    if (isMounted.current) setLoading(false)
  }, [url, activeTab, filters])

  /* ---- Fetch stats ---- */
  const fetchStats = useCallback(async () => {
    if (!url) return
    try {
      const res = await axios.get(`${url}/api/hmi/unclaimed-work/stats`)
      if (res.data?.success && isMounted.current) {
        setStats(res.data.data)
      }
    } catch (err) {
      console.error('[UW_PAGE] Failed to load stats:', err)
    }
  }, [url])

  /* Fetch on mount and when tab/filters change — abort stale requests */
  useEffect(() => {
    const controller = new AbortController()
    const doFetch = async () => {
      if (!url) return
      setLoading(true)
      try {
        const params = { state: TAB_STATE_MAP[activeTab] }
        if (filters.priority) params.priority = filters.priority
        if (filters.source) params.source = filters.source
        if (filters.service_type) params.service_type = filters.service_type
        params.sort = filters.sort === 'newest' ? 'newest' : 'oldest'

        const res = await axios.get(`${url}/api/hmi/unclaimed-work`, { params, signal: controller.signal })
        if (res.data?.success && isMounted.current) {
          setItems(res.data.data || [])
        }
      } catch (err) {
        if (!axios.isCancel(err)) console.error('[UW_PAGE] Failed to load items:', err)
      }
      if (isMounted.current) setLoading(false)
    }
    doFetch()
    return () => controller.abort()
  }, [url, activeTab, filters])

  useEffect(() => { fetchStats() }, [fetchStats])

  /* ---- Socket listeners ---- */
  useEffect(() => {
    const handleMessage = (data) => {
      if (!data?.type) return

      if (data.type === 'unclaimed_work_new' && data.payload) {
        if (activeTab === 'submitted') {
          setItems(prev => [data.payload, ...prev])
        }
        fetchStats()
      }

      if (data.type === 'unclaimed_work_assigned' && data.payload) {
        setItems(prev => prev.filter(i => i._id !== data.payload._id))
        fetchStats()
        // Toast if assigned to current user
        const assignedUserId = data.payload.assigned_to?._id || data.payload.assigned_to
        if (assignedUserId && assignedUserId.toString() === currentUserId?.toString()) {
          showToast(`Work item assigned to you: ${data.payload.title || 'Untitled'}`)
        }
      }

      if (data.type === 'unclaimed_work_completed' && data.payload) {
        setItems(prev => prev.filter(i => i._id !== data.payload._id))
        fetchStats()
      }

      if (data.type === 'unclaimed_work_updated' && data.payload) {
        setItems(prev => prev.map(i => i._id === data.payload._id ? data.payload : i))
      }
    }

    socket.on('message', handleMessage)
    return () => socket.off('message', handleMessage)
  }, [activeTab, currentUserId, fetchStats, showToast])

  /* ---- Event handlers ---- */
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab)
    setSelectedId(null)
  }, [])

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters)
  }, [])

  const handleSelect = useCallback((id) => {
    setSelectedId(id)
  }, [])

  const handleUpdate = useCallback(() => {
    fetchItems()
    fetchStats()
  }, [fetchItems, fetchStats])

  /* ---- Derived ---- */
  const selectedItem = items.find(i => i._id === selectedId) || null

  return (
    <div className="hmi-page dashboard-v2">
      <TopRail />

      {/* Page header */}
      <div className="uw-page-head">
        <div className="crumbs">
          <Link to="/hmi-v2">HMI</Link>
          <span className="sep">/</span>
          <span className="here">Work Queue</span>
        </div>
        <div className="uw-title-row">
          <h1>Unclaimed Work</h1>
          <span className="meta">
            <b>{stats.submitted + stats.assigned}</b> total items &middot; <b>{stats.urgent || 0}</b> urgent
          </span>
        </div>
      </div>

      {/* Stats strip */}
      <div className="uw-stats-strip">
        <div className="uw-stat-chip">
          <div>
            <div className="uw-stat-num risk">{stats.urgent || 0}</div>
            <div className="uw-stat-label">Urgent</div>
          </div>
        </div>
        <div className="uw-stat-chip">
          <div>
            <div className="uw-stat-num brand">{stats.submitted || 0}</div>
            <div className="uw-stat-label">Awaiting Assignment</div>
          </div>
        </div>
        <div className="uw-stat-chip">
          <div>
            <div className="uw-stat-num ink">{stats.assigned || 0}</div>
            <div className="uw-stat-label">In Progress</div>
          </div>
        </div>
        <div className="uw-stat-chip">
          <div>
            <div className="uw-stat-num ink">{stats.completedToday || 0}</div>
            <div className="uw-stat-label">Completed Today</div>
          </div>
        </div>
      </div>

      {/* Workspace */}
      <div className="uw-page">
        <div className="uw-workspace">
          {loading && items.length === 0 && (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
              Loading...
            </div>
          )}
          <div className="uw-split-view">
            <UnclaimedWorkList
              items={items}
              stats={stats}
              activeTab={activeTab}
              filters={filters}
              selectedId={selectedId}
              onTabChange={handleTabChange}
              onFilterChange={handleFilterChange}
              onSelect={handleSelect}
            />
            <UnclaimedWorkDetail
              item={selectedItem}
              url={url}
              currentUserId={currentUserId}
              onUpdate={handleUpdate}
            />
          </div>
        </div>
      </div>

      {/* Toast */}
      <div className={`uw-toast${toastVisible ? ' show' : ''}`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        {toastMessage}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated,
})

export default connect(mapStateToProps)(UnclaimedWorkPage)
